import { useMemo } from "react";
import { InfoIcon } from "lucide-react";
import type { ReportConfigV2 } from "@/common/report-configuration";
import type { DataItem } from "./ReportChartViewV2";
import {
  DimensionName,
  DIMENSIONS,
  findTimeDimensionV2,
} from "@/common/dimensions";

interface DisclaimersProps {
  data: DataItem[];
  reportConfig: ReportConfigV2;
  hasHiddenSmallGroups?: boolean;
}

export const DisclaimersV2: React.FC<DisclaimersProps> = ({
  data,
  reportConfig,
  hasHiddenSmallGroups,
}) => {
  const isTimeLimitHit = useMemo(() => {
    if (!data?.length || !reportConfig.chart.limit) return false;

    const timeDimension = findTimeDimensionV2(reportConfig.data);

    if (timeDimension) {
      const uniqueTimePeriods = new Set(
        data.map((item) => item[timeDimension.field]),
      );
      return uniqueTimePeriods.size > reportConfig.chart.limit;
    }

    return false;
  }, [data, reportConfig]);

  const isLimitHit = useMemo(() => {
    if (!data?.length || !reportConfig.chart.limit) return false;

    const timeDimension = findTimeDimensionV2(reportConfig.data);

    const checkGroupSize = (
      items: DataItem[],
      dims: DimensionName[],
    ): boolean => {
      if (!dims.length) return false;

      const currentDim = dims[0];
      // Skip time dimension as it's handled separately
      if (timeDimension && currentDim === timeDimension.dimension) {
        const nextDims = dims.slice(1);
        if (!nextDims.length) return false;
        return checkGroupSize(items, nextDims);
      }
      const dim = DIMENSIONS[currentDim];
      const groups = new Set(items.map((item) => item[dim.field]));
      if (groups.size > reportConfig.chart.limit) return true;

      const nextDims = dims.slice(1);
      if (!nextDims.length) return false;

      return Array.from(groups).some((group) =>
        checkGroupSize(
          items.filter((item) => item[currentDim] === group),
          nextDims,
        ),
      );
    };

    return checkGroupSize(data, reportConfig.data.dimensions);
  }, [data, reportConfig]);

  return (
    (isLimitHit ||
      isTimeLimitHit ||
      (hasHiddenSmallGroups && reportConfig.chart.hideSmallGroups)) && (
      <div className="bg-muted/50 p-3 rounded-md flex items-center gap-2 text-sm text-muted-foreground">
        <InfoIcon className="h-4 w-4" />
        <div className="ml-2">
          {isTimeLimitHit && (
            <p>
              Showing most recent {reportConfig.chart.limit} periods. Modify the
              limit in the Design settings to view more.
            </p>
          )}
          {isLimitHit && (
            <p className={isTimeLimitHit ? "mt-1" : ""}>
              Showing top {reportConfig.chart.limit} groups. Modify the limit in
              the Design settings to view more.
            </p>
          )}
          {hasHiddenSmallGroups && reportConfig.chart.hideSmallGroups && (
            <p className={isLimitHit || isTimeLimitHit ? "mt-1" : ""}>
              Groups with fewer than 5 people are hidden. Toggle "Hide Small
              Groups" in Design settings to view all groups.
            </p>
          )}
        </div>
      </div>
    )
  );
};
